import { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../redux-store/CustomReduxHooks";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import NotFound from "../../pages/NotFound/NotFound";
import { E_ChannelOptions } from "../../globalTypes/GlobalTypes";
import { updateLoadingState } from "../Feature_Loading/Loading_Slice";
import { isEmpty } from "lodash";
import { T_CreateOrderResponse, T_SummaryData } from "./Homeloan_Types";
import { useHLBaseDataLoader } from "./BaseDataInit/HomeLoan_Init_BaseData";
import CustomPolicyOrAccountSelect from "../../components/CustomPolicyOrAccountSelect/CustomPolicyOrAccountSelect";
import {
  HL_IdentifierConstant,
  HlLobCode,
  InAppFeatureFuncCodes,
} from "../../utils/constants";
import { R_HLRoutes } from "../../utils/Routes";
import { fetchSelectedHomeLoanIdentifierData } from "../../utils/conversionUtils";
import { hlIndexFinder } from "../../utils/helper";
import CustomFullScreenCircularLoader from "../../components/CustomCircularLoader/CustomFullScreenCircularLoader";

const HomeLoanGuard = () => {
  const [isError, setIsError] = useState<boolean>(false);
  const dispatch = useDispatch();
  const isReKycEvaluated = useRef(false);
  const { state, pathname, search } = useLocation();
  const csAppSlice = useAppSelector((st) => st.CsAppSlice);
  const allSummaryData: T_SummaryData[] = useAppSelector(
    (state) => state.HomeLoanSlice.summaryData,
  );
  const paymentInfo: T_CreateOrderResponse = useAppSelector(
    (state) => state.HomeLoanSlice.paymentInfo,
  );
  const accountNumber =
    state?.accountNumber || paymentInfo?.payment_data?.product_id || "";

  const homeLoanIdentifier = useAppSelector(
    (state) => state.HomeLoanSlice.homeLoanIdentifierData,
  );
  const secondaryChannelSlice = useAppSelector(
    (state) => state.SecondaryChannelSlice,
  );

  const idx = hlIndexFinder(allSummaryData, accountNumber);
  const summaryData = allSummaryData[Number(idx)];

  const navigate = useNavigate();
  const { isFetching, error } = useHLBaseDataLoader(accountNumber);

  const selectedHomeLoanIdentifierData = useMemo(() => {
    return fetchSelectedHomeLoanIdentifierData(
      homeLoanIdentifier,
      accountNumber,
    );
  }, [homeLoanIdentifier, accountNumber]);

  useEffect(() => {
    if (isFetching) {
      dispatch(updateLoadingState(true));
    } else {
      dispatch(updateLoadingState(false));
    }
  }, [isFetching]);

  useEffect(() => {
    if (
      csAppSlice?.channel === E_ChannelOptions.SECONDARY_CHANNEL &&
      secondaryChannelSlice?.functionalityCode ===
        InAppFeatureFuncCodes.HL_RE_KYC &&
      !isEmpty(allSummaryData) &&
      !isReKycEvaluated.current
    ) {
      const tempAccountNumber =
        allSummaryData?.[0]?.loan_info?.rows[0]?.LOAN_ACCOUNT_NUMBER || "";
      navigate(R_HLRoutes.home_loan_details, {
        replace: true,
        state: { accountNumber: tempAccountNumber },
      });
      isReKycEvaluated.current = true;
    }
  }, [homeLoanIdentifier, allSummaryData]);

  useEffect(() => {
    if (isEmpty(selectedHomeLoanIdentifierData)) {
      return;
    }
    if (R_HLRoutes.interest_certificate === pathname) {
      if (
        selectedHomeLoanIdentifierData?.HL_NHL_Tagging !==
          HL_IdentifierConstant[0] ||
        (selectedHomeLoanIdentifierData?.HL_NHL_Tagging ===
          HL_IdentifierConstant[0] &&
          !["Y", "P"].includes(
            summaryData?.loan_info?.rows[0]?.FINAL_DISB_FLAG,
          ))
      ) {
        setIsError(true);
      }
    } else if (R_HLRoutes.balance_confirmation_letter === pathname) {
      if (
        summaryData?.loan_info?.rows[0]?.ACCOUNT_STATUS?.toLowerCase() ===
        "closed"
      ) {
        setIsError(true);
      }
    }
  }, [pathname, selectedHomeLoanIdentifierData, summaryData, accountNumber]);

  const onPolicyAccountNumberSelection = (accountNumber: string) => {
    navigate(pathname + search, { replace: true, state: { accountNumber } });
  };

  if (
    isEmpty(accountNumber) &&
    !error &&
    !isFetching &&
    secondaryChannelSlice?.functionalityCode !== InAppFeatureFuncCodes.HL_RE_KYC
  ) {
    return (
      <CustomPolicyOrAccountSelect
        data={{
          isOpen: true,
          options: allSummaryData.map(
            (item) => item.loan_info.rows[0].LOAN_ACCOUNT_NUMBER,
          ),
          lobDetails: {
            lobId: HlLobCode,
            lobName: "",
            lob: HlLobCode,
            uniqueId: true,
          },
          redirectionUrl: "",
        }}
        policyOrAccountDataHandler={onPolicyAccountNumberSelection}
        fromGuard={true}
      />
    );
  }

  return error || isError ? (
    <NotFound
      hideButton={csAppSlice.channel != E_ChannelOptions.CSAPP}
      moduleName="HL"
      pageName="HomeLoanGuard"
      errorTitle=""
      errorMessage={error}
    />
  ) : isFetching ? (
    <CustomFullScreenCircularLoader />
  ) : (
    <Outlet />
  );
};

export default HomeLoanGuard;
